import React from "react";
import { Link, router } from "@inertiajs/react";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface LineItem {
  id: number;
  description: string;
  hours: number;
  price_per_hour: number;
  total: number;
}

interface Invoice {
  id: number;
  invoice_id: string;
  issue_date: string;
  due_date: string;
  subject?: string;
  from_company: string;
  invoice_for: string;
  amount_due: number;
  status: string;
  line_items: LineItem[];
}

interface Props {
  invoice: Invoice;
}

export default function Show({ invoice }: Props) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', text: 'Draft' },
      sent: { color: 'bg-blue-100 text-blue-800', text: 'Sent' },
      paid: { color: 'bg-green-100 text-green-800', text: 'Paid' },
      overdue: { color: 'bg-red-100 text-red-800', text: 'Overdue' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this invoice?")) {
      router.delete(`/invoices/${invoice.id}`);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <Link
          href="/invoices"
          className="text-gray-600 hover:text-gray-900 flex items-center"
        >
          ← Back to Invoices
        </Link>
        <div className="space-x-2">
          <a
            href={`/invoices/${invoice.id}.pdf`}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition inline-block"
            download
          >
            Download PDF
          </a>
          <Link
            href={`/invoices/${invoice.id}/edit`}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
          >
            Edit Invoice
          </Link>
          <button
            onClick={handleDelete}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition"
          >
            Delete Invoice
          </button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-3xl">INVOICE</CardTitle>
              <CardDescription className="text-lg mt-1">
                {invoice.invoice_id}
              </CardDescription>
              <div className="mt-2">
                {getStatusBadge(invoice.status)}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Issue Date</p>
              <p className="font-semibold">{formatDate(invoice.issue_date)}</p>
              <p className="text-sm text-muted-foreground mt-2">Due Date</p>
              <p className="font-semibold">{formatDate(invoice.due_date)}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                From
              </h3>
              <p className="mt-2 text-lg font-semibold text-gray-900">
                {invoice.from_company}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                Invoice For
              </h3>
              <p className="mt-2 text-lg font-semibold text-gray-900">
                {invoice.invoice_for}
              </p>
            </div>
          </div>

          {invoice.subject && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                Subject
              </h3>
              <p className="mt-2 text-gray-900">{invoice.subject}</p>
            </div>
          )}

          <div className="mt-8">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.line_items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.hours}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(item.price_per_hour)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(item.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} className="text-right font-semibold">
                    Amount Due:
                  </TableCell>
                  <TableCell className="text-right text-xl font-bold">
                    {formatCurrency(invoice.amount_due)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
